/**
 * PhoneE164 — branded type for E.164-formatted phone numbers.
 *
 * Compile-time safety: a `PhoneE164` is guaranteed to have been validated and
 * normalized by `toPhoneE164`. Never cast a raw string directly to `PhoneE164`.
 *
 * System boundaries:
 *  - Client form input → call `toPhoneE164(raw)` before sending to API.
 *  - API route handler → re-run `toPhoneE164(req.body.phoneE164)` on entry
 *    (the brand is a compile-time aid; it does not protect against malicious clients).
 *  - Firestore persistence → values stored are already `PhoneE164`.
 *
 * Rendering: never display a raw `PhoneE164` directly. Use `maskPhoneE164` for
 * privacy-preserving UI display and logging.
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';
/** Type guard for narrowing untrusted input. */
export function isPhoneE164(raw) {
    if (!raw || typeof raw !== 'string')
        return false;
    const parsed = parsePhoneNumberFromString(raw);
    return parsed !== undefined && parsed.isValid() && parsed.format('E.164') === raw;
}
/**
 * Construct a PhoneE164 from raw input. Throws if not a valid phone number.
 * Accepts free-form input (e.g. "(555) 123-4567" or "+1 555 123 4567") and
 * normalizes to canonical E.164 ("+15551234567").
 *
 * @param raw    User-supplied phone string.
 * @param defaultCountry  Optional ISO 3166-1 alpha-2 country (e.g. 'US') used
 *                        as parsing context when `raw` lacks an international
 *                        prefix. Defaults to 'US'.
 */
export function toPhoneE164(raw, defaultCountry = 'US') {
    if (!raw || typeof raw !== 'string') {
        throw new InvalidPhoneNumberError('Phone number is required');
    }
    const parsed = parsePhoneNumberFromString(raw, defaultCountry);
    if (!parsed || !parsed.isValid()) {
        throw new InvalidPhoneNumberError(`Invalid phone number: ${raw}`);
    }
    return parsed.format('E.164');
}
/**
 * Render-only mask for UI display and logs.
 * `+15551234567` → `+1 (•••) •••-4567`
 *
 * The only sanctioned way to display a verified phone in the UI.
 */
export function maskPhoneE164(phone) {
    const parsed = parsePhoneNumberFromString(phone);
    if (!parsed)
        return '•••';
    const national = parsed.nationalNumber.toString();
    const last4 = national.slice(-4);
    return `+${parsed.countryCallingCode} •••-${last4}`;
}
/** Thrown when `toPhoneE164` cannot validate the input. */
export class InvalidPhoneNumberError extends Error {
    constructor(message) {
        super(message);
        this.name = 'InvalidPhoneNumberError';
    }
}
/**
 * A number as it is dialled, E.164 (`+15551234567`), or null when it isn't a valid one. Scraped US numbers that
 * kept a '+' but lost their country code ("+7064446057") are read as the US numbers they are.
 */
export function dialablePhone(raw) {
    const text = raw.trim();
    const digits = text.replace(/\D/g, '');
    if (digits.length < 7)
        return null;
    const candidates = text.startsWith('+') && digits.length === 10 ? [`+1${digits}`, text] : [text];
    for (const candidate of candidates) {
        const parsed = parsePhoneNumberFromString(candidate, 'US');
        if (parsed && parsed.isValid())
            return parsed.format('E.164');
    }
    return null;
}
/** A number as people read it: national format for US numbers, international otherwise; as given when invalid. */
export function displayPhone(phone) {
    const dialable = dialablePhone(phone);
    const parsed = dialable ? parsePhoneNumberFromString(dialable) : undefined;
    if (!parsed)
        return phone;
    return parsed.countryCallingCode === '1' ? parsed.formatNational() : parsed.formatInternational();
}
